/**
 * MatchModal - Create/Edit match wizard (3 steps)
 *
 * Steps:
 * 1. Match Details - Basic info, teams, assignments
 * 2. FTP Destinations - Select FTP targets for photo delivery
 * 3. IPTC Configuration - Metadata templates for exported photos
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import Modal from '../../../../shared/components/ui/Modal';
import Button from '../../../../shared/components/ui/Button';
import Stepper from '../../../../shared/components/ui/Stepper/Stepper';
import {
  EVENT_TYPES,
  SPORTS,
  seasonOptions,
  computeSeason,
  resolveCompetitionSlug,
  formatSeasonLabel,
  getCompetitionLabel,
  deriveMatchName,
} from '../../utils/constants';
import useCompetitions from '../../hooks/useCompetitions';
import {
  SUBJECT_PRONOUNS,
  DEFAULT_SUBJECT_PRONOUN,
  deriveSubjectPronoun,
} from '../../utils/subjectPronoun';
import { toLocalDateTimeInput, shiftLocalDateTimeInput } from '../../utils/dateUtils';
import FtpSelectionStep from './FtpSelectionStep';
import IptcConfigStep from './IptcConfigStep';
import styles from './MatchModal.module.css';
import {
  searchTeams,
  searchMatchHubFixtures,
  fetchTeamByExternalId,
  fetchVenues,
  linkTeamToMatchHub,
} from '../../../../api';
import MatchHubSearchInput from '../../../../shared/components/MatchHubSearchInput';

/**
 * Shape of a default match day, relative to the START time.
 *
 * `start_time` is the SHOOTING WINDOW, not kick-off — the photographer is on
 * site well before the players are, in practice three hours ahead for
 * accreditation, position and warm-up. The window then runs to five hours
 * after kick-off, covering the match plus post-match and filing.
 *
 * Both are only ever suggestions: every field stays editable and nothing is
 * derived once the editor has typed a value of their own.
 */
const KICK_OFF_OFFSET_HOURS = 3;
const END_OFFSET_HOURS = 8;

const derivedKickOff = (startValue) =>
  shiftLocalDateTimeInput(startValue, KICK_OFF_OFFSET_HOURS);
const derivedEnd = (startValue) => shiftLocalDateTimeInput(startValue, END_OFFSET_HOURS);

/**
 * Free-text MatchHub fixture search (round 2). Rows surface league / status /
 * HOME-AWAY / date / score so a planner can pick at a glance. The editor types
 * `Feyenoord - Ajax`, `Feyenoord v Ajax`, `Feyenoord vs Ajax`, or just
 * `Feyenoord` — gateway parses with multi-separator support.
 */
function MatchHubSearch({ onSelect, onManualEntry }) {
  const renderRow = (fixture) => {
    const home = fixture.home?.name || '';
    const away = fixture.away?.name || '';
    const date = fixture.kickoff ? new Date(fixture.kickoff).toLocaleDateString() : '';
    const hasScore =
      fixture.goals?.home !== null &&
      fixture.goals?.home !== undefined &&
      fixture.goals?.away !== null &&
      fixture.goals?.away !== undefined;
    const score = hasScore ? `(${fixture.goals.home}-${fixture.goals.away})` : '';
    const status = fixture.status_long || fixture.status || '';
    const league = fixture.season?.competition?.name || '';
    return (
      <div className={styles.searchResult}>
        <span className={styles.resultTeams}>
          {home} vs {away}
        </span>
        <span className={styles.resultMeta}>
          {[league, date, score, status].filter(Boolean).join(' · ')}
        </span>
      </div>
    );
  };

  return (
    <div className={styles.matchHubSearch}>
      <div className={styles.searchHeader}>
        <label className={styles.label}>Link from MatchHub</label>
        <span className={styles.hint}>
          Type a fixture, e.g. <em>Feyenoord - Ajax</em>, <em>Feyenoord v Ajax</em>, or just{' '}
          <em>Feyenoord</em>.
        </span>
      </div>
      <MatchHubSearchInput
        searchFn={searchMatchHubFixtures}
        dataKey="matches"
        renderRow={renderRow}
        onSelect={(fx) => onSelect(fx)}
        placeholder="Feyenoord - Ajax…"
        inputClassName={styles.searchInput}
        listClassName={styles.searchResults}
        rowClassName={styles.searchResultRow}
      />

      <div className={styles.divider}>
        <span>or</span>
      </div>

      <Button variant="ghost" fullWidth onClick={onManualEntry}>
        Enter match details manually
      </Button>
    </div>
  );
}

/**
 * Inline create-with-override card shown for a fixture team that doesn't exist
 * locally. Editor edits the suggested name (`Excelsior` → `Excelsior Rotterdam`),
 * clicks Create + Link, and the new local Team is wired to the wizard via
 * `onCreated(team)`.
 */
function PendingTeamCard({ side, externalId, suggestedName, onCreated, onSkip, onCreateTeam }) {
  const [name, setName] = useState(suggestedName);
  const [teamType, setTeamType] = useState('club');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  const handleCreate = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Name is required');
      return;
    }
    setCreating(true);
    setError('');
    try {
      // Create the local Team first, then link to MatchHub (writes external_id
      // + per-company CompanyTeamNaming.confirmed_name in one go on the server).
      const team = await onCreateTeam({ name: trimmed, team_type: teamType });
      if (!team || !team.id) {
        throw new Error('Team creation did not return an id');
      }
      await linkTeamToMatchHub(team.id, externalId, trimmed);
      onCreated({ ...team, name: trimmed, external_id: String(externalId) });
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Create + link failed');
    } finally {
      setCreating(false);
    }
  };

  const sideLabel = side === 'home' ? 'Home team' : 'Away team';
  return (
    <div
      style={{
        border: '1px solid var(--accent-soft, rgba(0, 200, 150, 0.4))',
        background: 'rgba(0, 200, 150, 0.06)',
        borderRadius: 6,
        padding: 10,
        margin: '8px 0',
      }}
    >
      <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 6 }}>
        {sideLabel} <em>{suggestedName}</em> isn&apos;t in Matrix yet. Adjust the local name
        (the per-company override), then create + link to MatchHub.
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <input
          type="text"
          className={styles.searchInput}
          value={name}
          onChange={(e) => setName(e.target.value)}
          style={{ flex: 1 }}
        />
        <div className={styles.typeSegment} role="group" aria-label="Team type" style={{ width: 200 }}>
          {[
            ['club', 'Club'],
            ['national', 'National'],
          ].map(([type, text]) => (
            <button
              key={type}
              type="button"
              aria-pressed={teamType === type}
              className={`${styles.typeOption} ${teamType === type ? styles.typeOptionActive : ''}`}
              onClick={() => setTeamType(type)}
            >
              {text}
            </button>
          ))}
        </div>
        <Button variant="primary" onClick={handleCreate} loading={creating}>
          Create + Link
        </Button>
        <Button variant="ghost" onClick={onSkip}>
          Skip
        </Button>
      </div>
      {error && (
        <div style={{ fontSize: 12, color: 'var(--danger, #ff6b6b)', marginTop: 6 }}>{error}</div>
      )}
    </div>
  );
}

/**
 * Searchable team selector with option to create new
 */
export function TeamSelector({ label, value, onChange, teams, onCreateTeam }) {
  const [showCreate, setShowCreate] = useState(false);
  const [newTeamName, setNewTeamName] = useState('');
  const [newTeamCommon, setNewTeamCommon] = useState('');
  const [newTeamType, setNewTeamType] = useState('club');
  const [duplicate, setDuplicate] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [selectedObj, setSelectedObj] = useState(null);
  const dropdownRef = useRef(null);

  // Track the chosen team object so its name still shows for teams that aren't
  // in the (API-paginated) preloaded `teams` prop — e.g. one picked via search.
  const selectedTeam =
    selectedObj && String(selectedObj.id) === String(value)
      ? selectedObj
      : teams.find((t) => String(t.id) === String(value));

  // Server-side search: the preloaded `teams` list is capped by API pagination
  // (default 50), so client-side filtering can't see every team. Hit the backend
  // /teams/?search= via searchTeams, mirroring usePersonForm's debounced search.
  // The server matches the name AND the common name (C6), so typing how a team
  // is commonly called finds it. A <2-char query falls back to a short starter
  // list from the preloaded teams.
  const query = searchQuery.trim();
  useEffect(() => {
    if (!isOpen || query.length < 2) return undefined;
    const t = setTimeout(async () => {
      try {
        setResults(await searchTeams(query));
      } catch (e) {
        console.error('Team search failed', e);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [query, isOpen]);

  const filteredTeams = query.length >= 2 ? results : teams.slice(0, 10);

  // Before creating: does a team already carry this name or common name? René
  // (25-09-2026) created "Netherlands U21" as a club next to the national "The
  // Netherlands U21"; the new row had no players, so every Dutch player's
  // caption fell back to his club.
  const newName = newTeamName.trim();
  useEffect(() => {
    if (!showCreate || newName.length < 2) return undefined;
    const t = setTimeout(async () => {
      try {
        const wanted = newName.toLowerCase();
        const found = (await searchTeams(newName)).find(
          (tm) =>
            (tm.name || '').toLowerCase() === wanted ||
            (tm.common_name || '').toLowerCase() === wanted,
        );
        setDuplicate(found || null);
      } catch (e) {
        console.error('Team duplicate check failed', e);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [newName, showCreate]);
  const shownDuplicate = showCreate && newName.length >= 2 ? duplicate : null;

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (team) => {
    setSelectedObj(team || null);
    // The object rides along: the wizard derives the caption pronoun from the
    // team NAME, and a team picked via search is not in the `teams` prop.
    onChange(team ? String(team.id) : null, team || null);
    setIsOpen(false);
    setSearchQuery('');
  };

  const openCreate = (prefill = '') => {
    setNewTeamName(prefill);
    setNewTeamCommon('');
    setNewTeamType('club');
    setDuplicate(null);
    setShowCreate(true);
    setIsOpen(false);
    setSearchQuery('');
  };

  const closeCreate = () => {
    setShowCreate(false);
    setNewTeamName('');
    setNewTeamCommon('');
    setDuplicate(null);
  };

  const handleCreate = async () => {
    if (!newName) return;
    const newTeam = await onCreateTeam({
      name: newName,
      team_type: newTeamType,
      common_name: newTeamCommon.trim(),
    });
    closeCreate();
    if (newTeam) {
      setSelectedObj(newTeam);
      onChange(String(newTeam.id), newTeam);
    }
  };

  const handleUseDuplicate = () => {
    const team = shownDuplicate;
    closeCreate();
    if (team) handleSelect(team);
  };

  const typeLabel = (team) => (team?.team_type === 'national' ? 'National' : 'Club');
  const commonOf = (team) =>
    team?.common_name && team.common_name !== team.name ? team.common_name : '';

  return (
    <div className={styles.formGroup}>
      <label className={styles.label}>{label}</label>
      {!showCreate ? (
        <div className={styles.teamSelectRow} ref={dropdownRef}>
          <div className={styles.teamSearchWrapper}>
            <input
              type="text"
              className={styles.input}
              placeholder="Search team..."
              aria-label={`${label}: search team`}
              value={isOpen ? searchQuery : (selectedTeam?.name || '')}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                if (!isOpen) setIsOpen(true);
              }}
              onFocus={() => {
                setIsOpen(true);
                setSearchQuery('');
              }}
            />
            {value && !isOpen && (
              <button
                type="button"
                className={styles.teamClearButton}
                onClick={() => handleSelect(null)}
                title="Clear selection"
                aria-label="Clear selection"
              >
                x
              </button>
            )}
            {isOpen && (
              <div className={styles.teamDropdown}>
                {filteredTeams.length === 0 && (
                  <div className={styles.teamDropdownEmpty}>No teams found</div>
                )}
                {filteredTeams.map((team) => (
                  <button
                    key={team.id}
                    type="button"
                    className={styles.teamDropdownItem}
                    onClick={() => handleSelect(team)}
                    // The name is the button's name; type and common name are
                    // extra, so they describe it rather than rename it.
                    aria-label={team.name}
                    aria-description={[typeLabel(team), commonOf(team) && `common name ${commonOf(team)}`]
                      .filter(Boolean)
                      .join(', ')}
                  >
                    <span className={styles.teamDropdownItemRow}>
                      <span className={styles.teamDropdownItemText}>
                        <span>{team.name}</span>
                        {commonOf(team) && (
                          <span className={styles.teamDropdownMeta}>
                            common name · <b>{commonOf(team)}</b>
                          </span>
                        )}
                      </span>
                      <span
                        className={`${styles.teamTypeBadge} ${
                          team.team_type === 'national' ? styles.teamTypeBadgeNational : ''
                        }`}
                      >
                        {typeLabel(team)}
                      </span>
                    </span>
                  </button>
                ))}
                {query.length >= 2 && (
                  <button
                    type="button"
                    className={`${styles.teamDropdownItem} ${styles.teamDropdownCreate}`}
                    onClick={() => openCreate(query)}
                  >
                    + Create “{query}”…
                  </button>
                )}
              </div>
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            className={styles.teamAddButton}
            onClick={() => openCreate('')}
            title="Add new team"
            aria-label="Add new team"
          >
            +
          </Button>
        </div>
      ) : (
        <div className={styles.createTeamPanel}>
          <div className={styles.createTeamGrid}>
            <div className={styles.createTeamField}>
              <span className={styles.createTeamFieldLabel}>Name</span>
              <input
                type="text"
                className={styles.input}
                aria-label="New team name"
                placeholder="New team name..."
                value={newTeamName}
                onChange={(e) => setNewTeamName(e.target.value)}
                autoFocus
              />
            </div>
            <div className={styles.createTeamField}>
              <span className={styles.createTeamFieldLabel}>Common name (optional)</span>
              <input
                type="text"
                className={styles.input}
                aria-label="Common name"
                placeholder="e.g. Netherlands U21"
                value={newTeamCommon}
                onChange={(e) => setNewTeamCommon(e.target.value)}
              />
            </div>
          </div>
          <div className={styles.createTeamField}>
            <span className={styles.createTeamFieldLabel}>Type</span>
            <div className={styles.typeSegment} role="group" aria-label="Team type">
              {[
                ['club', 'Club'],
                ['national', 'National team'],
              ].map(([type, text]) => (
                <button
                  key={type}
                  type="button"
                  aria-pressed={newTeamType === type}
                  className={`${styles.typeOption} ${newTeamType === type ? styles.typeOptionActive : ''}`}
                  onClick={() => setNewTeamType(type)}
                >
                  {text}
                </button>
              ))}
            </div>
          </div>
          {shownDuplicate ? (
            <div className={styles.duplicateAlert} role="alert">
              <span className={styles.duplicateAlertTitle}>“{shownDuplicate.name}” already exists</span>
              <span className={styles.duplicateAlertBody}>
                {commonOf(shownDuplicate) && (
                  <>
                    Its common name is <b>{commonOf(shownDuplicate)}</b>.{' '}
                  </>
                )}
                {typeLabel(shownDuplicate)} team
                {typeof shownDuplicate.member_count === 'number'
                  ? `, ${shownDuplicate.member_count} players`
                  : ''}
                . A new team starts without players, so tagged players would be captioned with
                their club instead.
              </span>
              <div className={styles.createTeamActions} style={{ justifyContent: 'flex-start' }}>
                <Button variant="primary" size="sm" onClick={handleUseDuplicate}>
                  Use {shownDuplicate.name}
                </Button>
                <Button variant="ghost" size="sm" onClick={handleCreate}>
                  Create a new team anyway
                </Button>
              </div>
            </div>
          ) : (
            <p className={styles.createTeamHint}>
              Players are only captioned with this team when they are members of it. A new team
              has no players yet: add the squad in Manage → Teams.
            </p>
          )}
          <div className={styles.createTeamActions}>
            <Button variant="ghost" size="sm" onClick={closeCreate}>
              Cancel
            </Button>
            {!shownDuplicate && (
              <Button variant="primary" size="sm" onClick={handleCreate} disabled={!newName}>
                {newTeamType === 'national' ? 'Add national team' : 'Add club'}
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Multi-select checkbox list for user assignments
 */
function UserCheckboxList({ label, users, selectedIds, onChange, autoSelectedHint }) {
  const handleToggle = (userId) => {
    const idStr = String(userId);
    if (selectedIds.includes(idStr)) {
      onChange(selectedIds.filter((id) => id !== idStr));
    } else {
      onChange([...selectedIds, idStr]);
    }
  };

  return (
    <div className={styles.formGroup}>
      <label className={styles.label}>{label}</label>
      <div className={styles.checkboxList}>
        {users.length === 0 ? (
          <span className={styles.noUsers}>No users available</span>
        ) : (
          users.map((user) => (
            <label key={user.id} className={styles.checkboxItem}>
              <input
                type="checkbox"
                checked={selectedIds.includes(String(user.id))}
                onChange={() => handleToggle(user.id)}
                className={styles.checkbox}
              />
              <span className={styles.checkboxLabel}>{user.username || user.name}</span>
            </label>
          ))
        )}
      </div>
      {autoSelectedHint && (
        <span className={styles.autoFilled}>{autoSelectedHint}</span>
      )}
    </div>
  );
}

/**
 * Step 1: Match details form
 */
function MatchDetailsForm({
  formData,
  onChange,
  onTeamPicked,
  teams,
  photographers,
  editors,
  onCreateTeam,
  errors,
}) {
  // Competitions come from the operator's table; inactive rows stay out of
  // the dropdown unless the match being edited is filed under one.
  const { active: activeCompetitions, bySlug } = useCompetitions();
  const competitionOptions = useMemo(() => {
    const current = formData.competition ? bySlug[formData.competition] : null;
    return current && !current.active ? [...activeCompetitions, current] : activeCompetitions;
  }, [activeCompetitions, bySlug, formData.competition]);

  const updateField = useCallback((field, value) => {
    onChange((prev) => {
      const next = { ...prev, [field]: value };
      // Clear match-specific fields when switching away from match type
      if (field === 'event_type' && value !== 'match') {
        next.home_team = null;
        next.away_team = null;
        next.competition = null;
        next.kick_off_time = '';
        next.sport = 'football';
      }
      // Entering a match today meant typing the date three times. Derive the
      // other two from the start, which is the only one that is required.
      //
      // Only when the field is empty, or still holds exactly what the previous
      // start derived — that equality IS the "untouched" test, so no extra
      // state is needed to tell a default apart from a deliberate value. Move
      // the start to another day and untouched fields follow; edited ones stay
      // put and the min/validation below catch an inconsistency.
      if (field === 'start_time' && value) {
        if (!prev.end_time || prev.end_time === derivedEnd(prev.start_time)) {
          next.end_time = derivedEnd(value);
        }
        if (
          prev.event_type === 'match' &&
          (!prev.kick_off_time || prev.kick_off_time === derivedKickOff(prev.start_time))
        ) {
          next.kick_off_time = derivedKickOff(value);
        }
      }
      return next;
    });
  }, [onChange]);

  // Venue: free text with a stadium lookup against /api/venues/. Typing
  // only changes the name; PICKING a stadium fills city, state, country and
  // country code from the table (overwriting, because the pick is explicit).
  const [venueSuggestions, setVenueSuggestions] = useState([]);
  const [venueOpen, setVenueOpen] = useState(false);
  const venueQuery = formData.venue || '';
  useEffect(() => {
    if (!venueOpen || venueQuery.trim().length < 2) {
      setVenueSuggestions([]);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const res = await fetchVenues({ search: venueQuery.trim(), limit: 8 });
        if (!cancelled) setVenueSuggestions(res.results || []);
      } catch {
        if (!cancelled) setVenueSuggestions([]);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [venueQuery, venueOpen]);

  const handleVenueChange = useCallback((venue) => {
    onChange((prev) => ({ ...prev, venue }));
    setVenueOpen(true);
  }, [onChange]);

  const pickVenue = useCallback((v) => {
    onChange((prev) => ({
      ...prev,
      venue: v.name,
      city: v.city || '',
      state: v.state || '',
      country: v.country || '',
      country_code: v.country_code || '',
    }));
    setVenueOpen(false);
    setVenueSuggestions([]);
  }, [onChange]);

  // Auto-select photographer if only one available
  useEffect(() => {
    if (photographers.length === 1 && formData.photographer_ids.length === 0) {
      updateField('photographer_ids', [String(photographers[0].id)]);
    }
  }, [photographers, formData.photographer_ids, updateField]);

  return (
    <div className={styles.form}>
      {/* Event Type */}
      <div className={styles.formGroup}>
        <label className={styles.label}>Event Type</label>
        <select
          className={styles.select}
          value={formData.event_type || 'match'}
          onChange={(e) => updateField('event_type', e.target.value)}
        >
          {Object.entries(EVENT_TYPES).map(([key, { label }]) => (
            <option key={key} value={key}>{label}</option>
          ))}
        </select>
      </div>

      {/* Teams (only for match type) */}
      {formData.event_type === 'match' && (
        <div className={styles.formRow}>
          <TeamSelector
            label="Home Team *"
            value={formData.home_team}
            onChange={(v, team) => {
              onTeamPicked(team);
              updateField('home_team', v);
            }}
            teams={teams}
            onCreateTeam={onCreateTeam}
          />
          <TeamSelector
            label="Away Team *"
            value={formData.away_team}
            onChange={(v, team) => {
              onTeamPicked(team);
              updateField('away_team', v);
            }}
            teams={teams}
            onCreateTeam={onCreateTeam}
          />
        </div>
      )}

      {/* Caption pronoun: Match.subject_pronoun. Pre-filled from the team
          names (women's marker) on create, from the stored value on edit; an
          explicit choice here always wins and is what the goal phrases render. */}
      {formData.event_type === 'match' && (
        <div className={styles.formGroup}>
          <label className={styles.label} htmlFor="match-subject-pronoun">
            Caption pronoun
          </label>
          <select
            id="match-subject-pronoun"
            className={styles.select}
            value={formData.subject_pronoun || DEFAULT_SUBJECT_PRONOUN}
            onChange={(e) => updateField('subject_pronoun', e.target.value)}
          >
            {SUBJECT_PRONOUNS.map(({ value, label }) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
          <span className={styles.hint}>
            Goal phrases read &quot;
            {formData.subject_pronoun || DEFAULT_SUBJECT_PRONOUN} team&apos;s first goal&quot;.
            Suggested from the team names; pick &quot;their&quot; for a mixed fixture.
          </span>
        </div>
      )}

      {/* Event Name */}
      <div className={styles.formGroup}>
        <label className={styles.label}>
          {formData.event_type === 'match' ? 'Match Name *' : 'Event Name *'}
        </label>
        <input
          type="text"
          className={styles.input}
          placeholder={
            formData.event_type === 'match'
              ? 'Fills in from the teams, competition and season'
              : 'Event name...'
          }
          value={formData.name || ''}
          onChange={(e) => updateField('name', e.target.value)}
        />
        <span className={styles.hint}>
          Shown in every match list and in the app. Pre-filled; edit it if you want another name.
        </span>
        {errors?.name && <span className={styles.error}>{errors.name}</span>}
      </div>

      {/* Date/Time */}
      <div className={styles.formRow}>
        <div className={styles.formGroup}>
          <label className={styles.label}>Start Time *</label>
          <input
            type="datetime-local"
            className={styles.input}
            value={formData.start_time || ''}
            onChange={(e) => updateField('start_time', e.target.value)}
          />
          {errors?.start_time && <span className={styles.error}>{errors.start_time}</span>}
        </div>
        <div className={styles.formGroup}>
          <label className={styles.label}>End Time</label>
          <input
            type="datetime-local"
            className={styles.input}
            value={formData.end_time || ''}
            onChange={(e) => updateField('end_time', e.target.value)}
            min={formData.start_time || undefined}
          />
          {errors?.end_time && <span className={styles.error}>{errors.end_time}</span>}
        </div>
      </div>

      {/* Kick-off Time (match events only) */}
      {formData.event_type === 'match' && (
        <div className={styles.formGroup}>
          <label className={styles.label}>Kick-off Time</label>
          <input
            type="datetime-local"
            className={styles.input}
            value={formData.kick_off_time || ''}
            onChange={(e) => updateField('kick_off_time', e.target.value)}
            min={formData.start_time || undefined}
            max={formData.end_time || undefined}
          />
          <span className={styles.hint}>Actual kick-off. Used for before/during/after captions.</span>
        </div>
      )}

      {/* Venue and Location */}
      <div className={styles.formGroup} style={{ position: 'relative' }}>
        <label className={styles.label}>Venue</label>
        <input
          type="text"
          className={styles.input}
          placeholder="De Kuip"
          value={formData.venue || ''}
          autoComplete="off"
          onChange={(e) => handleVenueChange(e.target.value)}
          onFocus={() => setVenueOpen(true)}
          onBlur={() => setVenueOpen(false)}
        />
        {venueOpen && venueSuggestions.length > 0 && (
          <div className={`${styles.searchResults} ${styles.venueSuggestions}`} role="listbox">
            {venueSuggestions.map((v) => (
              <div
                key={v.id}
                className={styles.searchResult}
                role="option"
                aria-selected={false}
                // mousedown, not click: the input's blur closes the list first.
                onMouseDown={(e) => {
                  e.preventDefault();
                  pickVenue(v);
                }}
              >
                <span className={styles.resultTeams}>{v.name}</span>
                <span className={styles.resultMeta}>
                  {[v.club, v.city, v.country].filter(Boolean).join(' · ')}
                </span>
              </div>
            ))}
          </div>
        )}
        <span className={styles.hint}>
          Pick a stadium from the list to fill city, state and country
        </span>
      </div>

      {/* Location Fields */}
      <div className={styles.formRow}>
        <div className={styles.formGroup}>
          <label className={styles.label}>City</label>
          <input
            type="text"
            className={styles.input}
            placeholder="Rotterdam"
            value={formData.city || ''}
            onChange={(e) => updateField('city', e.target.value)}
          />
        </div>
        <div className={styles.formGroup}>
          <label className={styles.label}>State/Province</label>
          <input
            type="text"
            className={styles.input}
            placeholder="Zuid-Holland"
            value={formData.state || ''}
            onChange={(e) => updateField('state', e.target.value)}
          />
        </div>
      </div>
      <div className={styles.formRow}>
        <div className={styles.formGroup}>
          <label className={styles.label}>Country</label>
          <input
            type="text"
            className={styles.input}
            placeholder="Netherlands"
            value={formData.country || ''}
            onChange={(e) => updateField('country', e.target.value)}
          />
        </div>
        <div className={styles.formGroup}>
          <label className={styles.label}>Country Code</label>
          <input
            type="text"
            className={styles.input}
            placeholder="NLD"
            value={formData.country_code || ''}
            onChange={(e) => updateField('country_code', e.target.value)}
            maxLength={3}
          />
        </div>
      </div>

      {/* Competition (for matches) */}
      {formData.event_type === 'match' && (
        <>
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label className={styles.label}>Competition</label>
              <select
                className={styles.select}
                value={formData.competition || ''}
                onChange={(e) => updateField('competition', e.target.value || null)}
              >
                <option value="">Select competition...</option>
                {competitionOptions.map((c) => (
                  <option key={c.slug} value={c.slug}>{c.label}</option>
                ))}
                {/* Hybrid: a fixture's competition name that isn't in the
                    table is injected here so it stays selected + saved. */}
                {formData.competition && !bySlug[formData.competition] && (
                  <option value={formData.competition}>{formData.competition}</option>
                )}
              </select>
            </div>
            <div className={styles.formGroup}>
              <label className={styles.label}>Sport</label>
              <select
                className={styles.select}
                value={formData.sport || 'football'}
                onChange={(e) => updateField('sport', e.target.value)}
              >
                {Object.entries(SPORTS).map(([key, { label }]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>
          </div>
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label className={styles.label}>Season</label>
              <select
                className={styles.select}
                value={formData.season ?? ''}
                onChange={(e) => updateField('season', e.target.value)}
              >
                {seasonOptions().map(({ value, label }) => (
                  <option key={value || 'auto'} value={value}>{label}</option>
                ))}
                {/* Out-of-window / friendly seasons from a fixture prefill are
                    injected so they stay selected. */}
                {formData.season &&
                  !seasonOptions().some((o) => o.value === formData.season) && (
                    <option value={formData.season}>{formData.season}</option>
                  )}
              </select>
            </div>
            <div className={styles.formGroup}>
              <label className={styles.label}>Round</label>
              <input
                type="text"
                className={styles.input}
                value={formData.round || ''}
                onChange={(e) => updateField('round', e.target.value)}
                placeholder="12th, Quarterfinal, Round of 16..."
                maxLength={64}
              />
            </div>
          </div>
        </>
      )}

      {/* Assignments */}
      <div className={styles.formSection}>
        <h4 className={styles.sectionTitle}>Assignments</h4>
        <div className={styles.formRow}>
          <UserCheckboxList
            label="Photographers"
            users={photographers}
            selectedIds={formData.photographer_ids}
            onChange={(ids) => updateField('photographer_ids', ids)}
            autoSelectedHint={photographers.length === 1 ? 'Auto-selected (only photographer)' : null}
          />
          <UserCheckboxList
            label="Editors"
            users={editors}
            selectedIds={formData.editor_ids}
            onChange={(ids) => updateField('editor_ids', ids)}
          />
        </div>
      </div>

      {/* Processing stages — per-match pipeline toggles. Edit (Sati) always
          runs; turning off Crop/Tag skips that stage for this match's photos. */}
      <div className={styles.formSection}>
        <h4 className={styles.sectionTitle}>Processing</h4>
        <span className={styles.hint}>
          Turn off a stage to skip it for this match&apos;s photos.
        </span>
        <div className={styles.checkboxList}>
          <label className={styles.checkboxItem} title="Edit always runs">
            <input type="checkbox" className={styles.checkbox} checked readOnly disabled />
            <span className={styles.checkboxLabel}>Edit (always on)</span>
          </label>
          <label className={styles.checkboxItem}>
            <input
              type="checkbox"
              className={styles.checkbox}
              checked={formData.enable_crop !== false}
              onChange={(e) => updateField('enable_crop', e.target.checked)}
            />
            <span className={styles.checkboxLabel}>Crop</span>
          </label>
          <label className={styles.checkboxItem}>
            <input
              type="checkbox"
              className={styles.checkbox}
              checked={formData.enable_tag !== false}
              onChange={(e) => updateField('enable_tag', e.target.checked)}
            />
            <span className={styles.checkboxLabel}>Tag</span>
          </label>
        </div>
      </div>
    </div>
  );
}

// Default IPTC config
const DEFAULT_IPTC_CONFIG = {
  title_template: '',
  headline_template: '',
  caption_template: '',
  city: '',
  state: '',
  country: '',
  country_code: '',
  location: '',
  event_name: '',
  keywords: [],
  credit: '',
  source: '',
  transmission_reference: '',
  copyright_notice: '',
  category: 'S',
  supplemental_categories: ['SPO', 'SOC', 'FOC'],
  persons_template: '',
  caption_persons_template: '',
  organisation_template: '',
};

/**
 * Main MatchModal component - 3-step wizard
 */
export default function MatchModal({
  isOpen,
  onClose,
  onSave,
  match = null, // null for create, object for edit
  teams = [],
  photographers = [],
  editors = [],
  onCreateTeam,
  loading = false,
  // API functions for FTP and IPTC
  fetchFtpTargets,
  fetchGrades,
  fetchIptcTemplates,
  fetchCompanyDefaults,
  fetchIptcPreview,
}) {
  const [mode, setMode] = useState('search'); // 'search' | 'manual'
  // Pending MatchHub fixture link (set when an editor picks a fixture); applied
  // after the match is saved, since matchhub ids are set via link_matchhub.
  const [matchHubLink, setMatchHubLink] = useState(null);
  // When a chosen fixture references teams that don't exist locally yet, list
  // them here so the wizard can render an inline create-with-override card per
  // missing side. Cleared when the editor creates or skips each.
  const [pendingTeamCreates, setPendingTeamCreates] = useState([]); // [{side, externalId, suggestedName}]
  const [currentStep, setCurrentStep] = useState(1);
  // The A/B/C layout switcher was scaffolding for a design decision that has
  // now been made (destination rail). Clear the key so it does not linger in
  // every editor's browser forever.
  useEffect(() => {
    try {
      localStorage.removeItem('tank.iptcScopeLayout');
      localStorage.removeItem('tank.scope.layout');
    } catch {
      // Private mode; nothing to clean up.
    }
  }, []);

  const [formData, setFormData] = useState({
    event_type: 'match',
    name: '',
    home_team: null,
    away_team: null,
    start_time: '',
    end_time: '',
    venue: '',
    city: '',
    state: '',
    country: '',
    country_code: '',
    competition: null,
    sport: 'football',
    kick_off_time: '',
    season: '',
    round: '',
    photographer_ids: [],
    editor_ids: [],
    enable_crop: true,
    enable_tag: true,
    subject_pronoun: DEFAULT_SUBJECT_PRONOUN,
  });

  // Pre-fill the name as 'Home - Away Competition Season', the server's
  // Match.derive_name() rule. Only while the field is empty or still holds
  // exactly what the previous inputs derived: that equality is the
  // "untouched" test, so a typed name stays put and an existing match keeps
  // the name it was saved with.
  const { bySlug, competitions: allCompetitions } = useCompetitions();
  const lastDerivedNameRef = useRef('');
  useEffect(() => {
    const teamName = (id) => teams.find((t) => String(t.id) === String(id))?.name || '';
    const derived = deriveMatchName({
      eventType: formData.event_type,
      homeName: teamName(formData.home_team),
      awayName: teamName(formData.away_team),
      competitionLabel: formData.competition ? getCompetitionLabel(bySlug, formData.competition) : '',
      season: formData.season || computeSeason(formData.start_time),
      startTime: formData.start_time,
    });
    if (derived === lastDerivedNameRef.current) return;
    const untouched = !formData.name?.trim() || formData.name === lastDerivedNameRef.current;
    lastDerivedNameRef.current = derived;
    if (untouched && derived) updateForm((prev) => ({ ...prev, name: derived }));
  }, [
    formData.event_type,
    formData.home_team,
    formData.away_team,
    formData.competition,
    formData.season,
    formData.start_time,
    formData.name,
    teams,
    bySlug,
  ]);
  const [ftpTargetIds, setFtpTargetIds] = useState([]);
  // Export style per destination: {ftp_target_id: grade_id}. Only ever holds
  // entries for selected, non-default destinations (the API rejects others).
  const [ftpTargetGrades, setFtpTargetGrades] = useState({});
  // How each destination's metadata is written: {ftp_target_id: template_id}.
  // A missing entry means the company's house default, i.e. today's behaviour.
  const [ftpTargetIptc, setFtpTargetIptc] = useState({});
  // Whose uploads each destination receives: {ftp_target_id: [user_id]}.
  // A missing or empty entry means everyone -- never "nobody".
  const [ftpTargetPhotographers, setFtpTargetPhotographers] = useState({});
  // Where one destination disagrees with the rest: {ftp_target_id: {field: value}}.
  // CHANGED KEYS ONLY -- a full copy here would freeze that destination against
  // every later edit of the shared base in step 3.
  const [ftpTargetIptcOverrides, setFtpTargetIptcOverrides] = useState({});
  // Whether the company profile (house look at ingest + editor adjustments)
  // sits under the style: {ftp_target_id: boolean}. Mirrors ftpTargetGrades -
  // an entry only exists while that destination actually carries a style.
  const [ftpTargetIncludeProfile, setFtpTargetIncludeProfile] = useState({});
  const [iptcConfig, setIptcConfig] = useState(DEFAULT_IPTC_CONFIG);
  const [errors, setErrors] = useState({});

  // Step 2's code and step 3's override are the SAME value: both are
  // iptc_overrides[<the destination's reference field>]. The KEY differs per
  // destination -- two wires can want their code in different IPTC fields -- so
  // the card resolves it from the spec and passes it back here. MatchModal
  // never needs to hold the target list to do it.
  const handleReferenceChange = (targetId, field, value) => {
    const key = String(targetId);
    const current = { ...(ftpTargetIptcOverrides[key] || {}) };
    if (String(value).trim()) {
      current[field] = value;
    } else {
      // Blank clears the override rather than storing "", matching the API and
      // step 3: a stored blank would outrank the shared base.
      delete current[field];
    }
    setFtpTargetIptcOverrides({ ...ftpTargetIptcOverrides, [key]: current });
  };

  const isEdit = !!match;

  // Caption pronoun derivation. Team names for every team the wizard has seen
  // (search pick, create, MatchHub resolve), since `teams` is API-paginated.
  const teamNamesRef = useRef({});
  const rememberTeam = (team) => {
    if (team && team.id) teamNamesRef.current[String(team.id)] = team.name || '';
  };
  // Set once the editor picks a pronoun by hand; from then on a team change
  // never overwrites it, and the choice is sent as-is.
  const pronounTouchedRef = useRef(false);

  // Every form update passes through here so a team change on CREATE re-derives
  // the pronoun until the editor has chosen one. Edit never re-derives (the
  // server's update() does not either): the stored value is what shows.
  // Not wrapped in useCallback: the React Compiler memoizes it, and refuses
  // a manual wrapper around a ref write.
  const updateForm = (updater) => {
    setFormData((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      if (next.subject_pronoun !== prev.subject_pronoun) {
        pronounTouchedRef.current = true;
        return next;
      }
      if (isEdit || pronounTouchedRef.current) return next;
      if (next.home_team === prev.home_team && next.away_team === prev.away_team) return next;
      const nameOf = (id) =>
        teamNamesRef.current[String(id)] ??
        teams.find((t) => String(t.id) === String(id))?.name ??
        '';
      const derived = deriveSubjectPronoun(nameOf(next.home_team), nameOf(next.away_team));
      return derived === next.subject_pronoun ? next : { ...next, subject_pronoun: derived };
    });
  };

  // Initialize form data when editing - using a helper function to compute values
  // and then calling setters. This is intentional state sync from props.
  useEffect(() => {
    // Compute all new values first
    const initializeFromMatch = () => {
      if (match) {
        const photographerIds = (match.photographer_assignments || [])
          .map((a) => String(a.user_id));
        const editorIds = (match.editor_assignments || [])
          .map((a) => String(a.user_id));
        const ftpIds = (match.ftp_target_assignments || [])
          .map((a) => String(a.ftp_target_id));
        const ftpGrades = {};
        const ftpIncludeProfile = {};
        const ftpIptc = {};
        const ftpOverrides = {};
        const ftpPhotographers = {};
        (match.ftp_target_assignments || []).forEach((a) => {
          const key = String(a.ftp_target_id);
          if (a.grade_id) {
            ftpGrades[key] = String(a.grade_id);
            // Older rows predate the flag; missing means include (the default).
            ftpIncludeProfile[key] = a.include_company_profile !== false;
          }
          if (a.iptc_template_id) {
            ftpIptc[key] = String(a.iptc_template_id);
          }
          // Only store a real disagreement; {} and absent both mean "follows
          // the shared base".
          if (a.iptc_overrides && Object.keys(a.iptc_overrides).length > 0) {
            ftpOverrides[key] = a.iptc_overrides;
          }
          // Absent or empty both mean everyone; only store a real restriction.
          if (a.photographer_ids && a.photographer_ids.length > 0) {
            ftpPhotographers[key] = a.photographer_ids.map(String);
          }
        });

        return {
          formData: {
            event_type: match.event_type || 'match',
            name: match.name || '',
            home_team: match.home_team || null,
            away_team: match.away_team || null,
            start_time: toLocalDateTimeInput(match.start_time),
            end_time: toLocalDateTimeInput(match.end_time),
            kick_off_time: toLocalDateTimeInput(match.kick_off_time),
            venue: match.venue || '',
            city: match.iptc_config?.city || '',
            state: match.iptc_config?.state || '',
            country: match.iptc_config?.country || '',
            country_code: match.iptc_config?.country_code || '',
            competition: match.competition || null,
            sport: match.sport || 'football',
            season: match.season || '',
            round: match.round || '',
            photographer_ids: photographerIds,
            editor_ids: editorIds,
            enable_crop: match.enable_crop !== false,
            enable_tag: match.enable_tag !== false,
            subject_pronoun: match.subject_pronoun || DEFAULT_SUBJECT_PRONOUN,
          },
          ftpTargetIds: ftpIds,
          ftpTargetGrades: ftpGrades,
          ftpTargetIncludeProfile: ftpIncludeProfile,
          ftpTargetIptc: ftpIptc,
          ftpTargetIptcOverrides: ftpOverrides,
          ftpTargetPhotographers: ftpPhotographers,
          iptcConfig: match.iptc_config
            ? { ...DEFAULT_IPTC_CONFIG, ...match.iptc_config }
            : DEFAULT_IPTC_CONFIG,
          mode: 'manual',
          currentStep: 1,
        };
      }
      return {
        formData: null, // Keep existing
        ftpTargetIds: [],
        ftpTargetGrades: {},
        ftpTargetIncludeProfile: {},
        ftpTargetIptc: {},
        ftpTargetIptcOverrides: {},
        ftpTargetPhotographers: {},
        iptcConfig: DEFAULT_IPTC_CONFIG,
        mode: 'search',
        currentStep: 1,
      };
    };

    const values = initializeFromMatch();
    pronounTouchedRef.current = false;

    // Apply state updates (intentional sync from props on modal open)
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (values.formData) setFormData(values.formData);
    setFtpTargetIds(values.ftpTargetIds);
    setFtpTargetIptc(values.ftpTargetIptc || {});
    setFtpTargetIptcOverrides(values.ftpTargetIptcOverrides || {});
    setFtpTargetPhotographers(values.ftpTargetPhotographers || {});
    setFtpTargetGrades(values.ftpTargetGrades);
    setFtpTargetIncludeProfile(values.ftpTargetIncludeProfile);
    setIptcConfig(values.iptcConfig);
    setMode(values.mode);
    setCurrentStep(values.currentStep);
  }, [match, isOpen]);

  // FTP destination selection. Grades and their company-profile flags are
  // pruned here rather than at the call site so a deselected destination can
  // never keep a style in the payload - the API rejects a grade on an
  // unselected destination.
  const handleFtpTargetsChange = useCallback((ids) => {
    setFtpTargetIds(ids);
    setFtpTargetGrades((prev) => {
      const next = {};
      ids.forEach((id) => {
        const key = String(id);
        if (prev[key]) next[key] = prev[key];
      });
      return next;
    });
    setFtpTargetIncludeProfile((prev) => {
      const next = {};
      ids.forEach((id) => {
        const key = String(id);
        if (key in prev) next[key] = prev[key];
      });
      return next;
    });
  }, []);

  const handleFtpIptcChange = useCallback((targetId, templateId) => {
    const key = String(targetId);
    setFtpTargetIptc((prev) => {
      const next = { ...prev };
      if (templateId) {
        next[key] = String(templateId);
      } else {
        delete next[key];
      }
      return next;
    });
  }, []);

  const handleFtpPhotographersChange = useCallback((targetId, userIds) => {
    const key = String(targetId);
    setFtpTargetPhotographers((prev) => {
      const next = { ...prev };
      // Empty means everyone, so drop the key rather than storing [] -- an
      // empty list and an absent one must not read differently on the server.
      if (userIds && userIds.length > 0) {
        next[key] = userIds.map(String);
      } else {
        delete next[key];
      }
      return next;
    });
  }, []);

  const handleFtpGradeChange = useCallback((targetId, gradeId) => {
    const key = String(targetId);
    setFtpTargetGrades((prev) => {
      const next = { ...prev };
      if (gradeId) {
        next[key] = String(gradeId);
      } else {
        delete next[key];
      }
      return next;
    });
    // The flag is meaningless without a style: seed it at the default when one
    // is picked, drop it when the style is cleared.
    setFtpTargetIncludeProfile((prev) => {
      const next = { ...prev };
      if (gradeId) {
        if (!(key in next)) next[key] = true;
      } else {
        delete next[key];
      }
      return next;
    });
  }, []);

  const handleFtpIncludeProfileChange = useCallback((targetId, include) => {
    setFtpTargetIncludeProfile((prev) => ({ ...prev, [String(targetId)]: !!include }));
  }, []);

  // Validate step 1
  const validateStep1 = useCallback(() => {
    const errs = {};

    // The name is what every list and the app's match picker show, so it is
    // required for every event type (a blank one was a blank row, 11-09).
    if (!formData.name?.trim()) errs.name = 'Required';
    if (formData.event_type === 'match') {
      if (!formData.home_team) errs.home_team = 'Required';
      if (!formData.away_team) errs.away_team = 'Required';
    }

    if (!formData.start_time) errs.start_time = 'Required';

    // The input's `min` only stops the picker, not a pasted or typed value.
    if (formData.end_time && formData.start_time && formData.end_time < formData.start_time) {
      errs.end_time = 'Must be after start time';
    }

    if (formData.kick_off_time) {
      if (formData.start_time && formData.kick_off_time < formData.start_time) {
        errs.kick_off_time = 'Must be after start time';
      }
      if (formData.end_time && formData.kick_off_time > formData.end_time) {
        errs.kick_off_time = 'Must be before end time';
      }
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  }, [formData]);

  // Navigate to next step
  const handleNext = () => {
    if (currentStep === 1) {
      if (!validateStep1()) return;
    }
    setCurrentStep((prev) => Math.min(prev + 1, 3));
  };

  // Navigate to previous step
  const handleBack = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  // Handle save
  const handleSave = async () => {
    if (!validateStep1()) {
      setCurrentStep(1);
      return;
    }

    const data = {
      ...formData,
      start_time: new Date(formData.start_time).toISOString(),
      end_time: formData.end_time ? new Date(formData.end_time).toISOString() : null,
      kick_off_time: formData.kick_off_time
        ? new Date(formData.kick_off_time).toISOString()
        : null,
      // Coerce null competition to empty string (backend CharField doesn't accept null)
      competition: formData.competition || '',
      // Omit null team fields for non-match events
      home_team: formData.home_team || null,
      away_team: formData.away_team || null,
      // An explicit choice, or the stored value on edit, goes on the wire. An
      // untouched dropdown on create is omitted (undefined is dropped from the
      // JSON) so the server derives it with the pool-root knowledge this
      // client lacks: Match.derive_subject_pronoun is the source of truth.
      subject_pronoun:
        isEdit || pronounTouchedRef.current ? formData.subject_pronoun : undefined,
      // Convert string IDs to appropriate format for the API
      photographer_ids: formData.photographer_ids,
      editor_ids: formData.editor_ids,
      ftp_target_ids: ftpTargetIds,
      ftp_target_grades: ftpTargetGrades,
      ftp_target_include_profile: ftpTargetIncludeProfile,
      ftp_target_iptc: ftpTargetIptc,
      ftp_target_iptc_overrides: ftpTargetIptcOverrides,
      ftp_target_photographers: ftpTargetPhotographers,
      iptc_config: iptcConfig,
    };

    // matchHubLink (if any) is applied by the parent after the match is saved,
    // via the link_matchhub action (matchhub ids are read-only on the serializer).
    await onSave(data, matchHubLink);
  };

  // Handle MatchHub fixture selection. Resolves local Teams via external_id;
  // when one side has no local row, queues a PendingTeamCard so the editor
  // can create-with-override before continuing. Always derives the shooting
  // window (start = kickoff − 5h, end = kickoff + 5h) per the fixed rule.
  const handleMatchHubSelect = async (fixture) => {
    const home = fixture.home || {};
    const away = fixture.away || {};
    const kickoff = fixture.kickoff ? new Date(fixture.kickoff) : null;
    const startIso = kickoff ? new Date(kickoff.getTime() - 5 * 60 * 60 * 1000) : null;
    const endIso = kickoff ? new Date(kickoff.getTime() + 5 * 60 * 60 * 1000) : null;
    const seasonId = fixture.season?.id;

    // Resolve local home/away by external_id. Missing ones queue an inline
    // create card; the editor must address each before saving.
    const localHome = home.id ? await fetchTeamByExternalId(home.id).catch(() => null) : null;
    const localAway = away.id ? await fetchTeamByExternalId(away.id).catch(() => null) : null;
    const pending = [];
    if (home.id && !localHome) {
      pending.push({ side: 'home', externalId: String(home.id), suggestedName: home.name || '' });
    }
    if (away.id && !localAway) {
      pending.push({ side: 'away', externalId: String(away.id), suggestedName: away.name || '' });
    }
    setPendingTeamCreates(pending);

    const compName = fixture.season?.competition?.name;
    const venue = fixture.venue || {};
    rememberTeam(localHome);
    rememberTeam(localAway);
    updateForm((prev) => ({
      ...prev,
      home_team: localHome ? localHome.id : prev.home_team,
      away_team: localAway ? localAway.id : prev.away_team,
      kick_off_time: kickoff ? toLocalDateTimeInput(kickoff) : prev.kick_off_time,
      start_time: startIso ? toLocalDateTimeInput(startIso) : prev.start_time,
      end_time: endIso ? toLocalDateTimeInput(endIso) : prev.end_time,
      // Hybrid: snap a known competition name onto its curated slug (keeps IPTC
      // label + colour); otherwise keep the raw API name (free-text-safe, shown
      // via an injected <option> below).
      competition: resolveCompetitionSlug(allCompetitions, compName) || compName || prev.competition,
      // MatchHub season.year is the STARTING year (2024 ⇒ "2024/2025"), matching
      // the Season dropdown's option format.
      season: fixture.season?.year ? formatSeasonLabel(fixture.season.year) : prev.season,
      round: fixture.round || prev.round,
      // A2: prefill location from the fixture's venue. State/Province isn't
      // provided by Sportmonks, so it stays manual.
      venue: venue.name || prev.venue,
      city: venue.city || prev.city,
      country: venue.country || prev.country,
      country_code: venue.country_code || prev.country_code,
      sport: prev.sport || 'football',
    }));
    if (seasonId) {
      setMatchHubLink({ fixtureId: String(fixture.id), seasonId: String(seasonId) });
    }
    setMode('manual');
  };

  // PendingTeamCard onCreated callback — set the new Team on the corresponding
  // side and clear the card.
  const onPendingTeamCreated = (side, team) => {
    rememberTeam(team);
    updateForm((prev) => ({
      ...prev,
      [side === 'home' ? 'home_team' : 'away_team']: team.id,
    }));
    setPendingTeamCreates((prev) => prev.filter((p) => p.side !== side));
  };

  const onPendingTeamSkipped = (side) => {
    setPendingTeamCreates((prev) => prev.filter((p) => p.side !== side));
  };

  // Render current step content
  const renderStepContent = () => {
    if (mode === 'search' && !isEdit && currentStep === 1) {
      return (
        <MatchHubSearch
          onSelect={handleMatchHubSelect}
          onManualEntry={() => setMode('manual')}
        />
      );
    }

    switch (currentStep) {
      case 1:
        return (
          <>
            {pendingTeamCreates.map((p) => (
              <PendingTeamCard
                key={p.side}
                side={p.side}
                externalId={p.externalId}
                suggestedName={p.suggestedName}
                onCreateTeam={onCreateTeam}
                onCreated={(team) => onPendingTeamCreated(p.side, team)}
                onSkip={() => onPendingTeamSkipped(p.side)}
              />
            ))}
            <MatchDetailsForm
              formData={formData}
              onChange={updateForm}
              onTeamPicked={rememberTeam}
              teams={teams}
              photographers={photographers}
              editors={editors}
              onCreateTeam={onCreateTeam}
              errors={errors}
            />
          </>
        );
      case 2:
        return (
          <FtpSelectionStep
            selectedIds={ftpTargetIds}
            onChange={handleFtpTargetsChange}
            fetchFtpTargets={fetchFtpTargets}
            fetchGrades={fetchGrades}
            gradeByTarget={ftpTargetGrades}
            onGradeChange={handleFtpGradeChange}
            includeProfileByTarget={ftpTargetIncludeProfile}
            onIncludeProfileChange={handleFtpIncludeProfileChange}
            fetchIptcTemplates={fetchIptcTemplates}
            autoSelectDefaults={!match}
            iptcByTarget={ftpTargetIptc}
            onIptcChange={handleFtpIptcChange}
            photographers={photographers.filter((p) =>
              formData.photographer_ids.includes(String(p.id))
            )}
            photographersByTarget={ftpTargetPhotographers}
            overridesByTarget={ftpTargetIptcOverrides}
            onReferenceChange={handleReferenceChange}
            onPhotographersChange={handleFtpPhotographersChange}
          />
        );
      case 3:
        return (
          <IptcConfigStep
            config={iptcConfig}
            onChange={setIptcConfig}
            matchData={formData}
            teams={teams}
            fetchCompanyDefaults={fetchCompanyDefaults}
            fetchPreview={fetchIptcPreview}
            selectedTargetIds={ftpTargetIds}
            iptcByTarget={ftpTargetIptc}
            overridesByTarget={ftpTargetIptcOverrides}
            onOverridesChange={setFtpTargetIptcOverrides}
            fetchFtpTargets={fetchFtpTargets}
            fetchIptcTemplates={fetchIptcTemplates}
          />
        );
      default:
        return null;
    }
  };

  // Step labels
  const stepLabels = ['Match Details', 'FTP Destinations', 'IPTC Config'];

  return (
    // closeOnOverlay is off deliberately: this is a three-step form with no
    // draft, so a stray click on the backdrop used to discard everything typed
    // so far with no warning. Closing stays available through the header X and
    // the Cancel button, both of which are deliberate.
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      className={styles.drawerB}
      closeOnOverlay={false}
    >
      <Modal.Header onClose={onClose}>
        {isEdit ? 'Edit Match' : 'Create Match'}
      </Modal.Header>

      <Modal.Body className={styles.modalBody}>
        {/* Stepper - only show in manual mode */}
        {mode === 'manual' && (
          <Stepper currentStep={currentStep}>
            <Stepper.Step number={1} label={stepLabels[0]} />
            <Stepper.Step number={2} label={stepLabels[1]} />
            <Stepper.Step number={3} label={stepLabels[2]} />
          </Stepper>
        )}

        {renderStepContent()}
      </Modal.Body>

      <Modal.Footer>
        {/* Back button */}
        {mode === 'manual' && currentStep > 1 && (
          <Button variant="ghost" onClick={handleBack}>
            Back
          </Button>
        )}

        {/* Back to search button */}
        {mode === 'manual' && currentStep === 1 && !isEdit && (
          <Button variant="ghost" onClick={() => setMode('search')}>
            Back to Search
          </Button>
        )}

        <div style={{ flex: 1 }} />

        {/* Cancel button */}
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>

        {/* Next/Create button */}
        {mode === 'manual' && (
          <>
            {currentStep < 3 ? (
              <Button variant="primary" onClick={handleNext}>
                Next
              </Button>
            ) : (
              <Button variant="primary" onClick={handleSave} loading={loading}>
                {isEdit ? 'Save Changes' : 'Create Match'}
              </Button>
            )}
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
}
